import React, { useEffect, useState } from 'react'
import type { Toast as ToastType } from '../../contexts/ToastContext'
import { AlertIcon, CheckIcon, InfoIcon, XIcon } from './Icons'

interface ToastProps {
  toast: ToastType
  onClose: (id: string) => void
}

export const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Trigger slide-in animation
    requestAnimationFrame(() => {
      setIsVisible(true)
    })
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    // Wait for animation to complete before removing
    setTimeout(() => {
      onClose(toast.id)
    }, 300)
  }

  const typeConfig = {
    success: {
      bgColor: 'bg-success-500',
      textColor: 'text-white',
      icon: CheckIcon,
      ariaRole: 'status' as const,
    },
    error: {
      bgColor: 'bg-danger-500',
      textColor: 'text-white',
      icon: XIcon,
      ariaRole: 'alert' as const,
    },
    warning: {
      bgColor: 'bg-warning-500',
      textColor: 'text-ink-900',
      icon: AlertIcon,
      ariaRole: 'status' as const,
    },
    info: {
      bgColor: 'bg-info-500',
      textColor: 'text-white',
      icon: InfoIcon,
      ariaRole: 'status' as const,
    },
  }

  const config = typeConfig[toast.type]
  const Icon = config.icon

  return (
    <div
      role={config.ariaRole}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`
        ${config.bgColor} ${config.textColor}
        rounded-xl2 shadow-soft p-4 mb-3 min-w-[320px] max-w-md
        flex items-start gap-3
        transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <Icon className="flex-shrink-0 mt-0.5" size="md" />

      <p className="flex-1 text-sm font-medium leading-relaxed">
        {toast.message}
      </p>

      <button
        onClick={handleClose}
        className={`
          flex-shrink-0 p-1 rounded-lg
          hover:bg-black/10 transition-colors
          focus:outline-none focus:ring-2 focus:ring-white/50
        `}
        aria-label="Close notification"
      >
        <XIcon size="sm" />
      </button>
    </div>
  )
}
